import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import './styles.css';

/**
 * AnimatedPageTransition Component
 * 
 * Provides smooth slide-in and slide-out animations for page transitions using CSS
 * Respects user's prefers-reduced-motion settings for accessibility
 * 
 * @param {Object} props
 * @param {React.ReactNode} props.children - The page content to animate
 * @param {string} props.direction - Animation direction: 'left', 'right', 'up', 'down' (default: 'right')
 * @param {number} props.duration - Animation duration in milliseconds (default: 400)
 * @param {string} props.ease - Easing function (default: 'ease-in-out')
 */
export default function AnimatedPageTransition({ 
  children, 
  direction = 'right',
  duration = 400,
  ease = 'ease-in-out'
}) {
  const location = useLocation();
  const [displayChildren, setDisplayChildren] = useState(children);
  const [animationClass, setAnimationClass] = useState('enter');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  // Check for reduced motion preference
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  useEffect(() => {
    // Trigger exit animation
    setAnimationClass('exit');
    
    const effectiveDuration = prefersReducedMotion ? 0 : duration;
    
    // Wait for exit animation, then update children and trigger enter animation
    const timer = setTimeout(() => {
      setDisplayChildren(children);
      setAnimationClass('enter');
    }, effectiveDuration);

    return () => clearTimeout(timer);
  }, [location.pathname, children, duration, prefersReducedMotion]);

  // Map easing strings to CSS timing functions
  const getTimingFunction = (ease) => {
    const easingMap = {
      'linear': 'linear',
      'easeIn': 'ease-in',
      'easeOut': 'ease-out',
      'easeInOut': 'ease-in-out',
      'ease-in': 'ease-in',
      'ease-out': 'ease-out',
      'ease-in-out': 'ease-in-out',
    };
    return easingMap[ease] || 'ease-in-out';
  };

  const effectiveDuration = prefersReducedMotion ? 0 : duration;

  return (
    <div 
      className={`animated-page-transition animated-page-transition--${direction} animated-page-transition--${animationClass}`}
      style={{
        '--transition-duration': `${effectiveDuration}ms`,
        '--transition-timing': getTimingFunction(ease),
      }}
    >
      {displayChildren}
    </div>
  );
}

